// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setCutProductsModalOpen from "@steiner/App/Handlers/ProductDetails/SetCutProductsModalOpen";
import { CustomAppState, CutItem } from "@steiner/App/Store/Reducers";
import ProductDescription, { ProductDescriptionStyles } from "@steiner/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@steiner/Overrides/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@steiner/Overrides/Components/ProductPartNumbers";
import { addToCartStatus } from "@steiner/XDL/constants";
import { Divider, xValidateOnSubmit } from "@steiner/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    product: state.xCutProducts.product,
    isModalOpen: state.xCutProducts.isOpen,
    isProductDetailsPage: getCurrentPage(state)?.type === "ProductDetailsPage",
    canApproveOrders: getCurrentAccountState(state)?.value?.canApproveOrders,
    isOrderApprovalDetailsPage: getCurrentPage(state).type === "OrderApprovalDetailsPage",
});
const mapDispatchToProps = {
    setCutProductsModalOpen,
    updateCartLine,
    addToCart,
};

interface OwnProps {
    extendedStyles?: CutProductsModalStyles;
}

export interface CutProductsModalStyles {
    modal?: ModalPresentationProps;
    instructionsText?: TypographyPresentationProps;
    productWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productImageNoPointerStyles?: ProductImageStyles;
    descriptionWrapper?: InjectableCss;
    productTitle?: ProductDescriptionStyles;
    productNumbersWrapper?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    cutProductWrapper?: InjectableCss;
    cutHeaderRow?: InjectableCss;
    cutHeaderCutsText?: TypographyPresentationProps;
    cutHeaderFeetText?: TypographyPresentationProps;
    cutHeaderTotalText?: TypographyPresentationProps;
    cutItemRow?: InjectableCss;
    cutTextFieldStyles?: TextFieldPresentationProps;
    cutOperatorStyles?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    closeButton?: ButtonPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    disclaimerText?: TypographyPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const cutProductsModalStyles: CutProductsModalStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const CutProductsModal: React.FC<Props> = ({
    extendedStyles,
    product,
    isModalOpen,
    setCutProductsModalOpen,
    updateCartLine,
    addToCart,
    isProductDetailsPage,
    canApproveOrders,
    isOrderApprovalDetailsPage,
}) => {
    const [styles] = useState(() => mergeToNew(cutProductsModalStyles, extendedStyles));
    const [currentStatus, setCurrentStatus] = useState(addToCartStatus.default);
    const [localCut, setLocalCut] = useState<CutItem>({ cutsNo: "1", feets: "1" });
    const [cutsError, setCutsError] = useState<string | undefined>();
    const toasterContext = useContext(ToasterContext);
    const formId = "CutProductsForm";

    useEffect(() => {
        if (product && isModalOpen) {
            const qty = "product" in product ? product.productInfo.qtyOrdered : product.qtyOrdered;
            if ("properties" in product && product.properties?.cut) {
                const [cutsNo, feets] = product.properties.cut.split(",");
                setLocalCut({ cutsNo, feets });
            } else {
                setLocalCut({ cutsNo: "1", feets: String(qty ?? 1) });
            }
        }
    }, [product, isModalOpen]);

    if (!product) {
        return null;
    }
    const productNumber = "product" in product ? product.product.productNumber : product.erpNumber;
    const customerProductNumber = "product" in product ? product.product.customerProductNumber : product.customerName;
    const manufacturerItem = "product" in product ? product.product.manufacturerItem : product.manufacturerItem;

    const isButtonDisabled =
        currentStatus.status === addToCartStatus.loading.status ||
        !!cutsError ||
        Number(localCut.cutsNo) <= 0 ||
        Number(localCut.feets) <= 0;
    const handleAddToCart = () => {
        const cutProductsFormErrors = xValidateOnSubmit(formId);
        if (cutProductsFormErrors.length > 0) {
            return;
        }
        setCurrentStatus(addToCartStatus.loading);
        const qtyOrdered = Number(localCut.cutsNo) * Number(localCut.feets);
        if (!isProductDetailsPage && !("product" in product)) {
            updateCartLine({
                cartLine: {
                    ...product,
                    qtyOrdered,
                    properties: {
                        ...product.properties,
                        cut: `${localCut.cutsNo},${localCut.feets}`,
                    },
                },
                onSuccess: () => {
                    setCurrentStatus(addToCartStatus.default);
                    handleCloseModal();
                },
            });
        } else if (isProductDetailsPage && "product" in product) {
            addToCart({
                productId: product.product.id,
                unitOfMeasure: product.productInfo.unitOfMeasure,
                qtyOrdered,
                properties: {
                    cut: `${localCut.cutsNo},${localCut.feets}`,
                },
                onSuccess: () => {
                    setCurrentStatus(addToCartStatus.default);
                    toasterContext.addToast({ body: siteMessage("Cart_ProductAddedToCart"), messageType: "success" });
                    handleCloseModal();
                },
            } as any);
        }
    };

    const handleCloseModal = () => {
        setCutsError(undefined);
        setCutProductsModalOpen({ isOpen: false });
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Cut Lengths")}
            isOpen={isModalOpen}
            handleClose={handleCloseModal}
        >
            {isOrderApprovalDetailsPage && !canApproveOrders ? (
                <>
                    <Typography as="p" {...styles.disclaimerText}>
                        {siteMessage("Order_Approval_Cut_Products_Disclaimer")}
                    </Typography>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.cancelButton} onClick={handleCloseModal}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <Typography as="p" {...styles.instructionsText}>
                        {siteMessage("Custom_Cut_Length_Instructions")}
                    </Typography>
                    <Divider />
                    <StyledWrapper {...styles.productWrapper}>
                        <StyledWrapper {...styles.imageWrapper}>
                            <ProductImage
                                product={product}
                                onClick={() => {
                                    if (!isProductDetailsPage) {
                                        handleCloseModal();
                                    }
                                }}
                                extendedStyles={
                                    isProductDetailsPage ? styles.productImageNoPointerStyles : styles.productImage
                                }
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.descriptionWrapper}>
                            <ProductDescription
                                product={product}
                                readOnly={isProductDetailsPage}
                                onClick={handleCloseModal}
                                extendedStyles={styles.productTitle}
                            />
                            <StyledWrapper {...styles.productNumbersWrapper}>
                                <ProductPartNumbers
                                    productNumber={productNumber}
                                    customerProductNumber={customerProductNumber}
                                    manufacturerItem={manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <Divider />
                    <StyledWrapper {...styles.cutProductWrapper}>
                        <StyledWrapper {...styles.cutHeaderRow}>
                            <Typography {...styles.cutHeaderCutsText}>{translate("# of Cuts")}</Typography>
                            <Typography {...styles.cutHeaderFeetText}>{translate("Feet")}</Typography>
                            <Typography {...styles.cutHeaderTotalText}>{translate("Total")}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.cutItemRow}>
                            <TextField
                                type="qty"
                                placeholder={translate("0")}
                                value={localCut.cutsNo}
                                maxLength={3}
                                onChange={e => {
                                    setCutsError(undefined);
                                    setLocalCut(prev => ({ ...prev, cutsNo: e.target.value }));
                                    if (Number(e.target.value) > 100) {
                                        setCutsError("# of cuts cannot exceed 100.");
                                    }
                                }}
                                required
                                error={cutsError}
                                {...{ formId }}
                            />
                            <Typography {...styles.cutOperatorStyles}>{translate("x")}</Typography>
                            <TextField
                                type="qty"
                                placeholder={translate("0")}
                                value={localCut.feets}
                                onChange={e => {
                                    setLocalCut(prev => ({ ...prev, feets: e.target.value }));
                                }}
                                required
                                {...{ formId }}
                            />
                            <Typography {...styles.cutOperatorStyles}>{translate("=")}</Typography>
                            <TextField
                                type="qty"
                                placeholder={translate("0")}
                                value={
                                    !localCut.cutsNo || !localCut.feets
                                        ? ""
                                        : Number(localCut.cutsNo) * Number(localCut.feets)
                                }
                                readOnly
                                {...styles.cutTextFieldStyles}
                                {...{ formId }}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <Typography as="p" {...styles.disclaimerText}>
                        {siteMessage("Custom_Cut_Length_Disclaimer")}
                    </Typography>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.closeButton} onClick={handleCloseModal}>
                            {translate("Cancel")}
                        </Button>
                        <Button onClick={handleAddToCart} {...styles.addToCartButton} disabled={isButtonDisabled}>
                            {isProductDetailsPage ? translate(currentStatus.label || "Add to Cart") : translate("Save")}
                        </Button>
                    </StyledWrapper>
                </>
            )}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CutProductsModal);
